import { type ReactNode } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { cn } from '@/lib/cn';
import { formatCurrency, formatNumber } from '@/lib/formatters';
import { ScrollArea } from '@/components/ui/scroll-area';

interface KanbanColumnProps {
  id: string;
  title: string;
  count: number;
  totalValue: number;
  color?: string;
  items: string[];
  children: ReactNode;
}

export function KanbanColumn({
  id,
  title,
  count,
  totalValue,
  color,
  items,
  children,
}: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      className={cn(
        'flex w-72 flex-shrink-0 flex-col rounded-lg border bg-muted/30',
        isOver && 'ring-2 ring-primary',
      )}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between border-b p-3">
        <div className="flex items-center gap-2">
          {color && (
            <div className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
          )}
          <h3 className="text-sm font-semibold">{title}</h3>
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-xs font-medium">
            {count}
          </span>
        </div>
        <span className="text-xs text-muted-foreground">{formatCurrency(totalValue)}</span>
      </div>

      {/* Column Body */}
      <ScrollArea className="flex-1">
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <div ref={setNodeRef} className="min-h-[200px] space-y-2 p-2">
            {children}
          </div>
        </SortableContext>
      </ScrollArea>

      {/* Column Footer */}
      <div className="border-t p-2 text-center">
        <span className="text-xs text-muted-foreground">
          {formatNumber(count)} deal{count !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
}
